class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Each instance (desktop header, mobile modal) gets its own id prefix, because the results markup
    // is rendered from the same section and its ids would otherwise repeat on the page.
    PredictiveSearch.instanceCount = (PredictiveSearch.instanceCount || 0) + 1;
    this.idPrefix = `predictive-search-${PredictiveSearch.instanceCount}`;
    this.input?.setAttribute('aria-controls', `${this.idPrefix}-results`);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this?.input?.form?.addEventListener('submit', this?.onFormSubmit.bind(this));

    this?.input?.addEventListener('focus', this?.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // Keep focus in the input when the pointer presses a result, so the popup is not closed by the
    // focusout handler before the click lands (links and buttons do not take focus on click in Safari).
    this.predictiveSearchResults?.addEventListener('mousedown', (event) => event.preventDefault());
    // HFS (audit 2026-10-01, B06): any edit to the query drops the highlighted suggestion at once, before the
    // debounced request, so Enter during the debounce or a slow response cannot open a result of the old query.
    this?.input?.addEventListener('input', this.invalidateSelection.bind(this));
  }

  // Suggestions shown for the query now in the input, with no newer request pending.
  resultsCurrent() {
    return this.hasAttribute('results') && !this.hasAttribute('loading') && this.getQuery() === this.resultsTerm;
  }

  invalidateSelection() {
    if (this.getQuery() === this.resultsTerm) return;
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.removeAttribute('aria-activedescendant');
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    if (this.dismissedTerm !== this.searchTerm) this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.dismissedTerm = null;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && this.resultsTerm === currentSearchTerm) {
      this.open();
    } else {
      // No suggestions yet, or the retained ones answer an earlier query (focus left while the current query was
      // loading and that request was cancelled): fetch the current query instead of reopening stale results (B06).
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    // The popup consumes the whole first Escape press; a second press can close the search dialog.
    if (event.key === 'Escape' && this.escapeConsumed) {
      event.stopPropagation();
      this.escapeConsumed = false;
    }
  }

  onKeydown(event) {
    if (event.target !== this.input || event.isComposing) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
    }
    // Enter opens a highlighted suggestion only when the suggestions belong to the current query; otherwise the
    // form submits the typed query as an ordinary search (B06).
    if (event.key === 'Enter' && this.hasAttribute('open') && this.resultsCurrent() && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
    if (event.key === 'Escape' && (this.hasAttribute('open') || this.hasAttribute('loading'))) {
      event.preventDefault();
      event.stopPropagation();
      this.escapeConsumed = true;
      this.close();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.split(previousTerm).length !== 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') || !this.resultsCurrent()) return; // stale suggestions cannot be highlighted (B06)

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    activeElement.scrollIntoView?.({ block: 'nearest' });
  }

  selectOption() {
    const selectedOption = this.querySelector('a[aria-selected="true"], button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = this.requestId = (this.requestId || 0) + 1;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (requestId === this.requestId && this.getQuery() === searchTerm && this.contains(document.activeElement)) {
          this.renderSearchResults(resultsMarkup, searchTerm);
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20 || requestId !== this.requestId) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        // The ordinary search submit still works when the suggestion endpoint fails.
        this.setLiveRegionText('Suggestions are unavailable. Press Enter to search.');
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    clearTimeout(this.statusTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    this.statusTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.searchTerm) {
    this.resultsTerm = searchTerm; // the query these suggestions answer (B06)
    // Make the ids (and the aria-labelledby references to them) unique to this instance
    const markup = resultsMarkup.replace(/(\s(?:id|aria-labelledby)=")predictive-search-/g, `$1${this.idPrefix}-`);
    this.predictiveSearchResults.innerHTML = markup;
    this.input.removeAttribute('aria-activedescendant');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const bottom = this.input.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(0, window.innerHeight - bottom - 16);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.dismissedTerm = clearSearchTerm ? null : this.getQuery();
    this.abortController.abort();
    this.requestId = (this.requestId || 0) + 1;
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
